import {
  SOLAR_FINANCING_LOCKED_CODE,
  SOLAR_LIGHTREACH_BUSY_CODE,
  SOLAR_LIGHTREACH_CONTRACT_NOT_READY_CODE,
  SOLAR_LIGHTREACH_INELIGIBLE_CODE,
  SOLAR_LIGHTREACH_MARGIN_OUT_OF_BOUNDS_CODE,
  SOLAR_LIGHTREACH_NOT_OFFERED_CODE,
  SOLAR_LIGHTREACH_OFFER_UNAVAILABLE_CODE,
  SOLAR_LIGHTREACH_PRICING_NOT_READY_CODE,
  SOLAR_LIGHTREACH_PRICING_REQUIRED_CODE,
  SOLAR_LIGHTREACH_PROVIDER_FAILED_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessage,
} from "~/api/solar-proposal-errors"

export { SOLAR_LIGHTREACH_FINANCE_TYPE } from "./solar-finance-partner"
/** The lender a LightReach plan's financing summary names. */
export const SOLAR_LIGHTREACH_LENDER = `LightReach`

export const LIGHTREACH_FINANCING_STATUS_TITLE = `LightReach Financing Status`
export const LIGHTREACH_OFFER_HEADING = `LightReach offer`

// ─── Get LightReach pricing and the offers ──────────────────────────────────

export const GET_LIGHTREACH_PRICING_LABEL = `Get LightReach pricing`
export const GET_LIGHTREACH_PRICING_BUSY_LABEL = `Getting LightReach pricing`
/** The seller-facing out-of-date banner, and Refresh Pricing's refusal for a LightReach plan. */
export const LIGHTREACH_PRICING_OUT_OF_DATE = `LightReach pricing out of date — Get LightReach pricing`
/** The Updates panel's pricing row on a LightReach plan without a current offer. */
export const LIGHTREACH_UPDATE_STALE_LABEL = `Needs a current LightReach offer`
export const LIGHTREACH_NO_OFFERS_YET_PROMPT = `Get LightReach pricing to see LightReach's PPA and lease offers for this design.`
export const LIGHTREACH_EMPTY_OFFERS_PROMPT = `LightReach returned no offers for this design.`
export const LIGHTREACH_NOT_AVAILABLE_PROMPT = `LightReach isn't available for this proposal.`
export const LIGHTREACH_LOAD_ERROR = `Unable to load LightReach.`
export const LIGHTREACH_SUGGESTED_LABEL = `Suggested`
export const SHOW_ALL_LIGHTREACH_TIERS_LABEL = `Show all tiers`
export const SHOW_SUGGESTED_LIGHTREACH_TIERS_LABEL = `Show suggested tiers only`
export const showAllLightReachTiersLabel = (hidden: number): string =>
  `${SHOW_ALL_LIGHTREACH_TIERS_LABEL} (${hidden} more)`
export const CHOOSE_LIGHTREACH_OFFER_LABEL = `Choose offer`
export const CHOOSE_LIGHTREACH_OFFER_BUSY_LABEL = `Choosing offer`
export const LIGHTREACH_OFFER_CHOSEN_LABEL = `Offer chosen and priced`
export const LIGHTREACH_FETCHED_PREFIX = `Priced by LightReach`

// ─── The selected offer ─────────────────────────────────────────────────────

export const LIGHTREACH_SELECTED_OFFER_HEADING = `Selected offer`
export const LIGHTREACH_QUOTE_STATUS_LABEL: Record<
  `current` | `stale` | `unavailable`,
  string
> = {
  current: `Current`,
  stale: `Out of date`,
  unavailable: `Unavailable`,
}
export const LIGHTREACH_OFFER_LABEL = `Offer`
export const LIGHTREACH_TERMS_LABEL = `Terms`
export const LIGHTREACH_FIRST_YEAR_PAYMENT_LABEL = `Year-1 payment`
export const LIGHTREACH_KWH_RATE_LABEL = `Energy rate`
export const LIGHTREACH_TIER_LABEL = `Tier`
export const LIGHTREACH_MARGIN_LABEL = `Powur margin`
export const LIGHTREACH_MARGIN_NOTE = `Back-solved from LightReach's price for this tier.`
export const LIGHTREACH_SYSTEM_COST_LABEL = `LightReach system cost`

// ─── The application ────────────────────────────────────────────────────────

export const LIGHTREACH_APPLY_LABEL = `Apply with LightReach`
export const LIGHTREACH_OFFER_REQUIRED_PROMPT = `Get LightReach pricing and choose an offer on the Loan Product step before applying.`
export const OPEN_LIGHTREACH_LOAN_PRODUCT_LABEL = `Open Loan Product`
export const LIGHTREACH_NO_APPLICATION_PROMPT = `No LightReach application yet.`

// ─── Errors ─────────────────────────────────────────────────────────────────

/**
 * What each LightReach refusal says when the server sent no message of its
 * own. The server's message, when present, names the specific fix (the
 * margin bound, the stale input, each eligibility reason) and wins.
 */
export const LIGHTREACH_ERROR_FALLBACK: Readonly<Record<string, string>> = {
  [SOLAR_LIGHTREACH_PRICING_NOT_READY_CODE]: `Production and savings must be current before LightReach can price this design. Update them, then get LightReach pricing.`,
  [SOLAR_LIGHTREACH_PRICING_REQUIRED_CODE]: LIGHTREACH_PRICING_OUT_OF_DATE,
  [SOLAR_LIGHTREACH_MARGIN_OUT_OF_BOUNDS_CODE]: `LightReach's price for this offer needs a margin outside what you can set. Choose another offer.`,
  [SOLAR_LIGHTREACH_OFFER_UNAVAILABLE_CODE]: `That offer isn't among the current LightReach offers. Choose one of them.`,
  [SOLAR_LIGHTREACH_INELIGIBLE_CODE]: `This proposal doesn't meet LightReach's requirements.`,
  [SOLAR_LIGHTREACH_NOT_OFFERED_CODE]: `LightReach isn't offered for this proposal now. Choose another finance partner.`,
  [SOLAR_LIGHTREACH_BUSY_CODE]: `LightReach is already being updated for this proposal. Try again in a moment.`,
  [SOLAR_LIGHTREACH_CONTRACT_NOT_READY_CODE]: `The LightReach contract can't be sent yet.`,
  [SOLAR_LIGHTREACH_PROVIDER_FAILED_CODE]: `LightReach didn't answer. Try again.`,
  [SOLAR_FINANCING_LOCKED_CODE]: `Financing locks this proposal's selections.`,
}

export const GET_LIGHTREACH_PRICING_ERROR = `Unable to get LightReach pricing. Try again.`
export const CHOOSE_LIGHTREACH_OFFER_ERROR = `Unable to choose this offer. Nothing was changed.`

/**
 * A LightReach command's refusal: the server's own reason for a known code
 * (an ineligible proposal lists every reason), else that code's fallback,
 * else the command's generic copy.
 */
export const solarLightReachErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const code = solarProposalErrorCode(error)
  if (code == null || !(code in LIGHTREACH_ERROR_FALLBACK)) return fallback
  return (
    solarProposalErrorMessage(error) ??
    LIGHTREACH_ERROR_FALLBACK[code] ??
    fallback
  )
}
